import type { DocumentLink } from "@/models/document-link/DocumentLink";

function isWorkspaceOnOtherSide(
  link: DocumentLink,
  currentDocumentId: string
): boolean {
  const isOutgoing = link.originDocumentId === currentDocumentId;
  return isOutgoing
    ? Boolean(link.destinationWorkspaceId)
    : Boolean(link.originWorkspaceId);
}

export type WorkspaceReferenceVisibilityContext = {
  maxOtherSideRevisionByKey: Map<string, number>;
};

export function isOutgoingDocumentLink(
  link: DocumentLink,
  currentDocumentId: string
): boolean {
  return link.originDocumentId === currentDocumentId;
}

/**
 * Key for collapsing several links that point to the same workspace *file* across
 * revisions. API stores blob ids per revision, so blob id alone never groups;
 * server-resolved file names are stable for the same logical document.
 */
export function otherSideWorkspaceCollapseKey(
  link: DocumentLink,
  currentDocumentId: string
): string {
  const isOutgoing = isOutgoingDocumentLink(link, currentDocumentId);
  const wsId = isOutgoing
    ? link.destinationWorkspaceId
    : link.originWorkspaceId;
  const displayName = (
    isOutgoing ? link.destinationDocumentName : link.originDocumentName
  )?.trim();
  const blobId = isOutgoing
    ? link.destinationDocumentId
    : link.originDocumentId;

  if (wsId && displayName) {
    return `${wsId}\0${displayName.toLocaleLowerCase("da")}`;
  }
  if (wsId && blobId) {
    return `${wsId}\0blob:${blobId}`;
  }
  return `__unique:${link.id}`;
}

export function otherSideWorkspaceRevision(
  link: DocumentLink,
  currentDocumentId: string
): number | undefined {
  const isOutgoing = isOutgoingDocumentLink(link, currentDocumentId);
  return isOutgoing ? link.destinationVersion : link.originVersion;
}

export function otherSideIsActiveRevision(
  link: DocumentLink,
  currentDocumentId: string
): boolean | undefined {
  const isOutgoing = isOutgoingDocumentLink(link, currentDocumentId);
  return isOutgoing ? link.destinationIsActiveRevision : link.originIsActiveRevision;
}

/** Workspace links on the other end of references for the open document. */
export function collectDocumentWorkspaceReferenceLinks(
  allLinks: DocumentLink[],
  currentDocumentId: string
): DocumentLink[] {
  return allLinks.filter((link) => {
    const touches =
      link.originDocumentId === currentDocumentId ||
      link.destinationDocumentId === currentDocumentId;
    return touches && isWorkspaceOnOtherSide(link, currentDocumentId);
  });
}

/**
 * Max other-side revision per workspace file (collapse key), across incoming and outgoing.
 * Needed so a stale incoming link is hidden when an active outgoing link exists for the same file.
 */
export function buildWorkspaceReferenceVisibilityContext(
  allWorkspaceReferenceLinks: DocumentLink[],
  currentDocumentId: string
): WorkspaceReferenceVisibilityContext {
  const maxOtherSideRevisionByKey = new Map<string, number>();

  for (const link of allWorkspaceReferenceLinks) {
    const key = otherSideWorkspaceCollapseKey(link, currentDocumentId);
    const version = otherSideWorkspaceRevision(link, currentDocumentId);
    if (version === undefined) continue;
    const prev = maxOtherSideRevisionByKey.get(key);
    if (prev === undefined || version > prev) {
      maxOtherSideRevisionByKey.set(key, version);
    }
  }

  return { maxOtherSideRevisionByKey };
}

function shouldHideWorkspaceLinkForEarlierVersions(
  link: DocumentLink,
  currentDocumentId: string,
  context: WorkspaceReferenceVisibilityContext
): boolean {
  if (!isWorkspaceOnOtherSide(link, currentDocumentId)) {
    return false;
  }

  const key = otherSideWorkspaceCollapseKey(link, currentDocumentId);
  const version = otherSideWorkspaceRevision(link, currentDocumentId);
  const maxVersion = context.maxOtherSideRevisionByKey.get(key);
  if (
    version !== undefined &&
    maxVersion !== undefined &&
    version < maxVersion
  ) {
    return true;
  }

  return otherSideIsActiveRevision(link, currentDocumentId) === false;
}

/**
 * Sidebar and document-body visibility for workspace reference ends.
 * When `showEarlierWorkspaceVersions` is off, hides workspace links on an earlier
 * other-side revision than the max for the same collapse key (via
 * `visibilityContext`), links explicitly flagged non-active, then collapses
 * duplicate file names to the latest revision per key.
 */
export function filterWorkspaceReferenceLinksForDisplay(
  links: DocumentLink[],
  currentDocumentId: string,
  showEarlierWorkspaceVersions: boolean,
  visibilityContext?: WorkspaceReferenceVisibilityContext
): DocumentLink[] {
  if (showEarlierWorkspaceVersions) {
    return links;
  }

  const context =
    visibilityContext ??
    buildWorkspaceReferenceVisibilityContext(links, currentDocumentId);

  const withoutEarlierEnds = links.filter(
    (link) =>
      !shouldHideWorkspaceLinkForEarlierVersions(link, currentDocumentId, context)
  );
  return filterWorkspaceLinksToLatestRevision(
    withoutEarlierEnds,
    currentDocumentId
  );
}

/**
 * For the same workspace document on the other side, keep only link(s) pointing to
 * the active (current) revision. Falls back to highest revision number if no active
 * revision flag is available. Links without a version are kept only when no link in
 * the group has a version.
 * Caller should apply display ordering (e.g. sortLinksForDisplay) if needed.
 */
export function filterWorkspaceLinksToLatestRevision(
  links: DocumentLink[],
  currentDocumentId: string
): DocumentLink[] {
  if (links.length <= 1) return links;

  const byKey = new Map<string, DocumentLink[]>();
  for (const link of links) {
    const key = otherSideWorkspaceCollapseKey(link, currentDocumentId);
    if (!byKey.has(key)) byKey.set(key, []);
    byKey.get(key)!.push(link);
  }

  const picked: DocumentLink[] = [];
  for (const [, group] of byKey) {
    if (group.length === 1) {
      picked.push(group[0]);
      continue;
    }

    // First, try to find links marked as active revision
    const activeFlags = group.map((link) =>
      otherSideIsActiveRevision(link, currentDocumentId)
    );
    const hasActiveFlags = activeFlags.some((flag) => flag === true);
    
    if (hasActiveFlags) {
      // If we have active revision flags, use only those marked as active
      for (let i = 0; i < group.length; i++) {
        if (activeFlags[i] === true) {
          picked.push(group[i]);
        }
      }
      continue;
    }

    // Fallback: use the old logic based on max version number
    const versions = group.map((link) =>
      otherSideWorkspaceRevision(link, currentDocumentId)
    );
    const defined = versions.filter((v): v is number => v !== undefined);
    if (defined.length === 0) {
      picked.push(...group);
      continue;
    }
    const maxV = Math.max(...defined);
    for (let i = 0; i < group.length; i++) {
      if (versions[i] === maxV) {
        picked.push(group[i]);
      }
    }
  }

  return picked;
}
